import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import Button from '~/src/components/Button'
import OnboardingTitle from '~/src/components/onboarding/OnboardingTitle'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import {
  OnboardingStep,
  useOnboardingActions,
} from '~/src/stores/useOnboardingStore'

type PersonalInfoFormData = {
  firstName: string
  email: string
}

const PersonalInfoStep: React.FC = () => {
  const analytics = useAnalytics()
  const { authUser } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const { userData, refetchUserData } = useAuthContext()

  const { featureOn: rewardsProgramFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RewardsProgram
  )

  const [processing, setProcessing] = useState(false)

  const stripeSubscriptionId = userData?.metadata?.stripeSubscriptionId

  const nextStep = useOnboardingActions().nextStep
  const markStepComplete = useOnboardingActions().markStepComplete
  const subscribeNewsletter = useOnboardingActions().subscribeNewsletter

  // Helper function to determine next step
  const getNextStep = () => {
    // Skip invite code step if feature is disabled or user already has invitedById
    const userInvitedById = userData?.invitedById
    if (!rewardsProgramFeatureEnabled || !!userInvitedById) {
      return OnboardingStep.COMPLETE
    }
    return OnboardingStep.INVITE_CODE
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PersonalInfoFormData>({
    defaultValues: {
      firstName: userData?.firstName || authUser?.displayName || '',
      email: userData?.email || authUser?.email || '',
    },
  })

  const handleContinue = async (formData: PersonalInfoFormData) => {
    if (!authUser?.uid) {
      return
    }

    setProcessing(true)

    // Track analytics
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'newsletter',
      skip: false,
      stripeSubscriptionId,
    })

    // Subscribe to newsletter (this also updates user data)
    const { status: subscribeStatus, error: subscribeError } =
      await subscribeNewsletter({
        authUser: {
          uid: authUser.uid,
          accessToken: authUser.accessToken,
          getIdToken: authUser.getIdToken?.bind(authUser),
        },
        tempUserId,
        email: formData.email,
        firstName: formData.firstName,
        refetchUserData,
      })

    if (!subscribeStatus) {
      toast.error(subscribeError || 'Error subscribing to newsletter')
      setProcessing(false)
      return
    }

    setProcessing(false)

    markStepComplete(OnboardingStep.WELCOME)
    nextStep(getNextStep())
  }

  const handleSkip = async () => {
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'newsletter',
      skip: true,
    })

    markStepComplete(OnboardingStep.WELCOME)
    nextStep(getNextStep())
  }

  return (
    <div className='space-y-6'>
      {/* <div className='max-w-xs space-y-2'>
        <div className='text-center text-3xl font-bold'>
          Get our top 5 finds once a week
        </div>
        <div className='opacity-50'>
          As a special bonus, you get to see our fav finds out of thousands of
          picks. Highly recommended!
        </div>
      </div> */}

      <OnboardingTitle
        title={
          <>
            Get our top 5<br />
            finds once a week
          </>
        }
        description='As a special bonus, you get to see our fav finds out of thousands of picks. Highly recommended!'
        disableBack
        className='mx-auto max-w-xs text-center'
        titleClassName='text-2xl md:text-4xl'
      />

      <form onSubmit={handleSubmit(handleContinue)} className='space-y-3'>
        <div className='space-y-2'>
          <input
            type='text'
            {...register('firstName', { required: 'First name is required' })}
            placeholder='First name'
            className='h-14 w-full rounded-full border border-black/20 !bg-white/50 px-4 py-3 text-black transition-all placeholder:text-black/30 focus:border-black/50'
          />
          {errors.firstName && (
            <span className='text-sm text-red-500'>
              {errors.firstName.message}
            </span>
          )}
        </div>

        <div className='space-y-2'>
          <input
            type='email'
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            placeholder='Email'
            className='h-14 w-full rounded-full border border-black/20 !bg-white/50 px-4 py-3 text-black transition-all placeholder:text-black/30 focus:border-black/50'
          />
          {errors.email && (
            <span className='text-sm text-red-500'>{errors.email.message}</span>
          )}
        </div>

        <div className='flex flex-col items-center justify-center space-y-6'>
          <Button
            type='submit'
            disabled={processing}
            size='lg'
            fullWidth
            rounded
          >
            {processing ? (
              <div className='animate-spin'>
                <LoaderIcon height={18} />
              </div>
            ) : (
              'Continue'
            )}
          </Button>
          <button
            type='button'
            onClick={handleSkip}
            disabled={processing}
            className='opacity-30 md:hover:underline'
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  )
}

export default PersonalInfoStep
